import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaTrash, FaMinus, FaPlus } from 'react-icons/fa';
import { getCurrencyByLocation, formatPrice } from '../utils/currency';

export default function Cart() {
  const { t, i18n } = useTranslation();
  const { cart, removeFromCart, updateCartQuantity, cartTotal } = useContext(AppContext);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4 text-[#0A2540]">{t('cart.empty')}</h2>
          <Link to="/shop">
            <Button className="btn-primary">{t('cart.continueShopping')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="cart-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8 text-[#0A2540]" data-testid="cart-title">
          {t('cart.title')}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => {
              const price = item.type === 'new' ? item.product.price_new :
                           item.type === 'used' ? item.product.price_used :
                           item.product.price_rental;
              const currency = getCurrencyByLocation(item.product.location);
              
              return (
                <Card key={`${item.product.id}-${item.type}`} data-testid={`cart-item-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {item.product.images && item.product.images[0] && (
                        <img 
                          src={item.product.images[0]} 
                          alt={i18n.language === 'fr' ? item.product.name_fr : item.product.name_en}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1">
                          {i18n.language === 'fr' ? item.product.name_fr : item.product.name_en}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {t(`product.${item.type}`)}
                        </p>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateCartQuantity(item.product.id, item.type, item.quantity - 1)}
                              data-testid={`decrease-qty-${index}`}
                            >
                              <FaMinus />
                            </Button>
                            <span className="font-semibold" data-testid={`quantity-${index}`}>{item.quantity}</span>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateCartQuantity(item.product.id, item.type, item.quantity + 1)}
                              data-testid={`increase-qty-${index}`}
                            >
                              <FaPlus />
                            </Button>
                          </div>
                          <span className="font-bold text-[#0A2540]" data-testid={`item-price-${index}`}>
                            {formatPrice(price * item.quantity, currency)}
                          </span>
                        </div>
                      </div>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => removeFromCart(item.product.id, item.type)}
                        data-testid={`remove-item-${index}`}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-6 text-[#0A2540]">{t('cart.total')}</h2>
                <div className="space-y-4 mb-6">
                  {(() => {
                    // Calculate totals by currency
                    const totals = { EUR: 0, USD: 0 };
                    cart.forEach(item => {
                      const price = item.type === 'new' ? item.product.price_new :
                                   item.type === 'used' ? item.product.price_used :
                                   item.product.price_rental;
                      const currency = getCurrencyByLocation(item.product.location);
                      totals[currency] += price * item.quantity;
                    });
                    
                    return (
                      <>
                        {totals.EUR > 0 && (
                          <div className="flex justify-between text-lg">
                            <span>Total EUR:</span>
                            <span className="font-bold text-[#0A2540]">{formatPrice(totals.EUR, 'EUR')}</span>
                          </div>
                        )}
                        {totals.USD > 0 && (
                          <div className="flex justify-between text-lg">
                            <span>Total USD:</span>
                            <span className="font-bold text-[#0A2540]">{formatPrice(totals.USD, 'USD')}</span>
                          </div>
                        )}
                        {totals.EUR > 0 && totals.USD > 0 && (
                          <div className="text-xs text-gray-500 mt-2">
                            {i18n.language === 'fr' 
                              ? '* Prix dans différentes devises selon la localisation' 
                              : '* Prices in different currencies based on location'}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <Link to="/checkout">
                  <Button className="w-full btn-primary" data-testid="checkout-btn">
                    {t('cart.checkout')}
                  </Button>
                </Link>
                <Link to="/shop">
                  <Button variant="outline" className="w-full mt-3">
                    {t('cart.continueShopping')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}